"use client";

import { useEffect, useState, type ReactNode } from "react";
import { NowBadge } from "@/components/now-badge";
import { isWindowActive, venueLocalNow, type HappyHourWindow, type OpenPeriod } from "@/lib/geo/timezone";

/**
 * One happy-hour card on the venue page. The heading's "Live now" says *a* window is on; with
 * several cards (a 5–7pm happy hour and a 12–2am late-night one) the reader had to work out which.
 * The live card gets the Live-now green border + label and floats to the top via CSS `order`
 * (the parent list is a flex column), so the ISR-cached markup stays time-free. Same client-side,
 * venue-tz, re-check-each-minute logic as VenueLiveDot.
 */
export function VenueHappyHourCard({
  window: w,
  hoursJson,
  timezone,
  children,
}: {
  window: HappyHourWindow;
  hoursJson: OpenPeriod[] | null;
  timezone: string | null;
  children: ReactNode;
}) {
  const [live, setLive] = useState(false);
  const days = w.daysOfWeek.join(",");

  useEffect(() => {
    if (!timezone) return;
    const win: HappyHourWindow = { ...w, daysOfWeek: days.split(",").map(Number) };
    const check = () => setLive(isWindowActive(win, venueLocalNow(timezone, new Date()), hoursJson));
    check();
    const id = setInterval(check, 60_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the window's primitive fields
  }, [days, w.allDay, w.startTime, w.endTime, w.crossesMidnight, hoursJson, timezone]);

  return (
    <li
      aria-current={live ? "true" : undefined}
      className={`rounded-lg border bg-bg-surface p-4 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.45)] ${
        live ? "-order-1 border-accent-live shadow-[0_0_18px_-6px_var(--accent-live)] ring-1 ring-accent-live/60" : "border-border"
      }`}
    >
      {live && (
        <p className="mb-2 inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-accent-live">
          <NowBadge open />
          Live now
        </p>
      )}
      {children}
    </li>
  );
}
